import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const NEWS = [
  {
    source: "Edmonton Journal",
    region: "Canada",
    title: "'The worst it's ever been': Alberta ERs buckle under pressure",
    url: "https://edmontonjournal.com/news/local-news/the-worst-its-ever-been-alberta-implements-triage-liaison-physicians-as-emergency-rooms-continue-to-buckle-under-pressure",
  },
  {
    source: "CBC News",
    region: "Canada",
    title: "Inside Canada's hospital wait-time crisis",
    url: "https://www.cbc.ca/news/canada/canada-hospitals-wait-times-9.7123684",
  },
  {
    source: "MedPage Today",
    region: "United States",
    title: "Second Opinions: the real cost of the waiting room",
    url: "https://www.medpagetoday.com/opinion/second-opinions/103166",
  },
  {
    source: "JHEOR",
    region: "United States",
    title: "Stranded in the ER: seniors await hospital care and suffer avoidable harm",
    url: "https://jheor.org/post/2483-stranded-in-the-er-seniors-await-hospital-care-and-suffer-avoidable-harm",
  },
  {
    source: "CBC News",
    region: "Canada",
    title: "Hospital patients waiting 48 hours in ERs for admission",
    url: "https://www.cbc.ca/news/health/hospital-patients-waiting-48-hours-for-admission-9.7248089",
  },
  {
    source: "CTV News",
    region: "Canada",
    title: "The true cause of Canada's massive emergency wait times",
    url: "https://www.ctvnews.ca/health/article/what-a-new-report-reveals-about-the-true-cause-of-canadas-massive-emergency-wait-times/",
  },
  {
    source: "CNN",
    region: "United States",
    title: "Emergency room wait times keep climbing across America",
    url: "https://www.cnn.com/2024/04/26/health/emergency-room-wait-times-kff-partner-content-wellness",
  },
  {
    source: "Associated Press",
    region: "United States",
    title: "ER boarding crisis leaves seniors waiting days for a bed",
    url: "https://apnews.com/article/er-wait-boarding-hospital-dementia-f906664b9c47ea603c9a49b99efbe36e",
  },
  {
    source: "BBC",
    region: "United Kingdom",
    title: "Record 12-hour A&E trolley waits as NHS backlog bites",
    url: "https://www.bbc.com/news/articles/c3dzez1g451o",
  },
  {
    source: "Euronews",
    region: "Europe",
    title: "Healthcare waiting times in Europe: the worst countries ranked",
    url: "https://www.euronews.com/2026/06/03/waiting-times-for-healthcare-in-europe-the-worst-countries-ranked",
  },
];

const ARTICLES = [
  {
    img: "/media/lifestyle-2.png",
    date: "October 2026",
    tag: "The Problem",
    title: "The waiting room is broken.",
    paragraphs: [
      "Every year, millions of people put off a basic health check for one simple reason: it takes too long. You call, wait weeks for an appointment, then sit in a crowded room full of sick strangers — sometimes for hours — for a checkup that takes minutes.",
      "The cost of that delay is real. High blood pressure quietly becomes heart disease. A small warning sign becomes an emergency room visit. Studies keep showing the same thing: when care is hard to reach, people skip it — and they get sicker because of it.",
      "We think that's unacceptable. A basic checkup should be as easy as grabbing a coffee.",
    ],
    testid: "article-waiting-room",
  },
  {
    img: "/media/hero.png",
    date: "October 2026",
    tag: "The Announcement",
    title: "The first in North America to take on the waiting room.",
    paragraphs: [
      "Today SANTELQ introduces the Hub — the first self-service health terminal of its kind in North America built to eliminate the waiting room entirely. No appointment. No paperwork. No crowded room.",
      "You walk in, and in about 60 seconds the Hub checks your heart, blood pressure, oxygen and more. Smart AI cameras look for early warning signs. If something needs attention, a real doctor joins you on video, right there, and can diagnose you on the spot.",
      "Your results go straight to your care team's records system, and you walk out with a printed summary in your hand. The whole visit takes less time than finding parking at a clinic.",
    ],
    testid: "article-first",
  },
  {
    img: "/media/feature-vitals.png",
    date: "September 2026",
    tag: "The Science",
    title: "What a 60-second checkup catches early.",
    paragraphs: [
      "Most serious conditions don't start with pain. They start quietly — a creeping blood pressure, an irregular heartbeat, falling oxygen levels. Catching them early is the single biggest lever in modern medicine, and it only works if people actually get checked.",
      "That's the whole idea behind the Hub. When a checkup takes 60 seconds and sits in the lobby of your workplace or clinic, people do it. Often. And the more often you're checked, the earlier problems get caught — when they're still small, treatable and cheap to fix.",
      "Every Hub also cleans and sanitizes itself after every visitor, so the next person walks into a fresh space, every time.",
    ],
    testid: "article-early",
  },
];

export default function Stories() {
  return (
    <main data-testid="stories-page">
      <Nav />

      <section className="pt-36 sm:pt-44 pb-24 px-6 sm:px-12 max-w-6xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.3 }}
          className="font-display font-semibold tracking-tight text-[#3A3A3C] text-4xl sm:text-5xl"
          data-testid="stories-title"
        >
          Stories
        </motion.h1>

        <div className="mt-16 space-y-24" data-testid="stories-list">
          {ARTICLES.map((a, i) => (
            <motion.article
              key={a.testid}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 1, ease: EASE }}
              className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
              }`}
              data-testid={a.testid}
            >
              <div className="rounded-[2rem] overflow-hidden aspect-[4/3]">
                <img src={a.img} alt={a.title} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#808080]">
                  {a.tag} · {a.date}
                </p>
                <h2 className="font-display font-semibold tracking-tight text-[#3A3A3C] text-2xl sm:text-3xl leading-[1.15] mt-3">
                  {a.title}
                </h2>
                {a.paragraphs.map((p, j) => (
                  <p key={j} className="text-sm sm:text-base text-[#474747] leading-relaxed mt-4">
                    {p}
                  </p>
                ))}
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-28" data-testid="in-the-news">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: EASE }}
            className="font-display font-semibold tracking-tight text-[#3A3A3C] text-2xl sm:text-3xl"
          >
            In the news
          </motion.h2>
          <p className="text-sm text-[#808080] mt-2">The waiting-room crisis, reported around the world.</p>
          <div className="mt-8">
            {NEWS.map((n) => (
              <a
                key={n.url}
                href={n.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-${n.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${n.region.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex items-baseline justify-between gap-6 py-5 border-b border-black/5 hover:bg-black/[0.015] transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-5">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#808080] whitespace-nowrap">
                    {n.source} · {n.region}
                  </span>
                  <span className="font-display font-medium tracking-tight text-[#3A3A3C] text-base sm:text-lg group-hover:translate-x-1 transition-transform duration-300">
                    {n.title}
                  </span>
                </div>
                <ArrowUpRight className="w-4 h-4 shrink-0 self-center text-[#808080] group-hover:text-[#3A3A3C] transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
